import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon, Button, Input, Tooltip } from 'components/common';
import { Space, Alert } from 'antd';
import copy from 'copy-to-clipboard';
import styled from 'styled-components';

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  color: #161616;
  button: hover svg {
    color: #40a9ff;
  }
  button: disabled svg {
    color: #d9d9d9;
  }
  margin-right: -8px;
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const ActionsButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledAlert = styled(Alert)`
  display: ${p => (p.isVisible ? 'flex' : 'none')};
  background-color: #2e2e2e;
  border: none;
`;

const FeedbackInputWrapper = styled(Space.Compact).withConfig({
  shouldForwardProp: prop => prop !== 'isCommenting',
})`
  width: 100%;
  display: ${p => (p.isCommenting ? 'flex' : 'none')};
`;

const WarningText = styled.span`
  color: #8d8d8d;
  font-size: 13px;
`;

function MessageActions({
  disabled,
  isStreaming,
  value,
  feedback,
  onFeedbackChange,
  logMessageInteraction,
  loading,
  scrollToBottom,
}) {
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [commented, setCommented] = useState(false);

  const handleCopy = () => {
    copy(value);
    setCopied(true);
  };

  const handleLiked = () => {
    if (liked) return;
    setLiked(true);
    setDisliked(false);
    setCommenting(true);
    logMessageInteraction({ liked: true });
    if (scrollToBottom) {
      scrollToBottom();
    }
  };

  const handleDisliked = () => {
    if (disliked) return;
    setDisliked(true);
    setLiked(false);
    setCommenting(true);
    logMessageInteraction({ disliked: true });
    if (scrollToBottom) {
      scrollToBottom();
    }
  };

  const handleCommenting = () => {
    if (commented) return;
    setCommenting(!commenting);
    if (!commenting && scrollToBottom) {
      scrollToBottom();
    }
  };

  const handleCommented = e => {
    setCommented(true);
    logMessageInteraction({ comment: e });
  };

  const handleTooltipOpen = visible => {
    if (visible) {
      setCopied(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && feedback?.length > 0) {
      e.preventDefault();
      handleCommented(feedback);
    }
  };

  useEffect(() => {
    onFeedbackChange('');
    setLiked(false);
    setDisliked(false);
    setCommenting(false);
    setCommented(false);
  }, [loading, onFeedbackChange, value]);

  // Add effect to scroll when commenting state changes
  useEffect(() => {
    if (commenting && scrollToBottom) {
      scrollToBottom();
    }
  }, [commenting, scrollToBottom]);

  return (
    <>
      <ActionsContainer>
        <WarningText>
          AI relies on patterns and data inputs, so errors may occur.
        </WarningText>
        <ActionsButtonsWrapper>
          <ButtonContainer
            onClick={e => e?.stopPropagation()}
            style={{ display: disliked ? 'none' : 'inherit' }}
          >
            <Tooltip
              title={!value ? '' : 'Like'}
              placement='left'
              mouseLeaveDelay={0}
            >
              <Button
                disabled={disabled || isStreaming}
                type='link'
                icon={
                  <Icon
                    icon={liked ? 'thumbsUp' : 'thumbsUpRegular'}
                    color={liked ? '#40a9ff' : '#595959'}
                    size='sm'
                  />
                }
                onClick={() => {
                  if (!loading && !disabled && !isStreaming) {
                    handleLiked();
                  }
                }}
              ></Button>
            </Tooltip>
          </ButtonContainer>

          <ButtonContainer
            onClick={e => e?.stopPropagation()}
            style={{ display: liked ? 'none' : 'inherit' }}
          >
            <Tooltip
              title={!value ? '' : 'Dislike'}
              placement='left'
              mouseLeaveDelay={0}
            >
              <Button
                disabled={disabled || isStreaming}
                type='link'
                icon={
                  <Icon
                    icon={disliked ? 'thumbsDown' : 'thumbsDownRegular'}
                    color={disliked ? '#40a9ff' : '#595959'}
                    size='sm'
                  />
                }
                onClick={() => {
                  if (!loading && !disabled && !isStreaming) {
                    handleDisliked();
                  }
                }}
              ></Button>
            </Tooltip>
          </ButtonContainer>

          {(liked || disliked) && (
            <ButtonContainer onClick={e => e?.stopPropagation()}>
              <Tooltip
                title={!value ? '' : 'Comment'}
                placement='left'
                mouseLeaveDelay={0}
              >
                <Button
                  type='link'
                  disabled={disabled || isStreaming}
                  icon={
                    <Icon
                      icon={commenting ? 'commenting' : 'commentingRegular'}
                      color={commented ? '#40a9ff' : '#595959'}
                      size='sm'
                    />
                  }
                  onClick={() => {
                    if (!loading && !disabled && !isStreaming) {
                      handleCommenting();
                    }
                  }}
                ></Button>
              </Tooltip>
            </ButtonContainer>
          )}

          <ButtonContainer onClick={e => e?.stopPropagation()}>
            <Tooltip
              title={!value ? '' : copied ? 'Copied' : 'Copy message'}
              onOpenChange={handleTooltipOpen}
              placement='left'
              mouseLeaveDelay={0}
            >
              <Button
                type='link'
                disabled={disabled || isStreaming}
                icon={<Icon icon='copy' color='#595959' size='sm' />}
                onClick={() => {
                  if (!loading && !disabled && !isStreaming) {
                    handleCopy();
                  }
                }}
              ></Button>
            </Tooltip>
          </ButtonContainer>
        </ActionsButtonsWrapper>
      </ActionsContainer>
      <FeedbackInputWrapper isCommenting={commenting && !commented}>
        <Input
          maxLength={200}
          placeholder='Tell us more about what you thought of this message'
          value={feedback}
          onChange={onFeedbackChange}
          onKeyDown={handleKeyDown}
          showCount
          multiline
          autoSize={{ minRows: 1, maxRows: 10 }}
          style={{ width: '100%' }}
        />

        <Button
          type='primary'
          disabled={!feedback?.length || feedback?.length === 0}
          onClick={() => handleCommented(feedback)}
        >
          Submit
        </Button>
      </FeedbackInputWrapper>
      <StyledAlert
        key={commented}
        isVisible={commented}
        showIcon
        closable
        closeIcon={<Icon icon='close' size='lg' style={{ color: 'white' }} />}
        message={
          <span style={{ color: 'white' }}>
            Thanks for your feedback, it helps us make the tool better!
          </span>
        }
        icon={<Icon icon='circleInfo' size='sm' style={{ color: 'white' }} />}
      ></StyledAlert>
    </>
  );
}

MessageActions.propTypes = {
  disabled: PropTypes.bool,
  isStreaming: PropTypes.bool,
  value: PropTypes.string,
  feedback: PropTypes.string,
  onFeedbackChange: PropTypes.func.isRequired,
  logMessageInteraction: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

MessageActions.defaultProps = {
  disabled: false,
  isStreaming: false,
  feedback: '',
  value: '',
};

export default MessageActions;
